/*
**************
* VIBE CODED *
**************
*/


import { getFunctions, httpsCallable } from "firebase/functions";
import React, { useCallback, useEffect, useState, useRef } from "react";
import style from "./stylesheets/RafflePage.module.scss";
import Button from "./Button";
import Input from "./Input";
import confetti from "canvas-confetti";

const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()";

const SlotMachineText = ({ text, isRevealed, onComplete, delay = 0 }) => {
    const [displayArray, setDisplayArray] = useState(
        Array(text.length).fill("").map((_, i) => text[i] === ' ' ? ' ' : CHARS[Math.floor(Math.random() * CHARS.length)])
    );
    const [lockedIndices, setLockedIndices] = useState(0);
    const internalReveal = useRef(false);

    useEffect(() => {
        let timeout;
        if (isRevealed) {
            timeout = setTimeout(() => {
                internalReveal.current = true;
            }, delay);
        }
        return () => clearTimeout(timeout);
    }, [isRevealed, delay]);

    useEffect(() => {
        const interval = setInterval(() => {
            setDisplayArray(prev => prev.map((char, i) => {
                if (text[i] === ' ') return ' ';
                if (internalReveal.current && i < lockedIndices) {
                    return text[i];
                }
                return CHARS[Math.floor(Math.random() * CHARS.length)];
            }));
            
            if (internalReveal.current) {
                setLockedIndices(prev => {
                    if (prev >= text.length) {
                        clearInterval(interval);
                        if (onComplete) onComplete();
                        return prev;
                    }
                    return prev + 0.5; // Slow down the locking slightly (every other tick)
                });
            }
        }, 50);

        return () => clearInterval(interval);
    }, [text, lockedIndices, onComplete]);

    const renderWords = () => {
        const words = text.split(" ");
        let globalIndex = 0;
        
        return words.map((word, wIdx) => {
            const wordElement = (
                <span key={`w-${wIdx}`} className={style.word}>
                    {word.split("").map((_, cIdx) => {
                        const idx = globalIndex + cIdx;
                        const charToDisplay = displayArray[idx];
                        const isLocked = internalReveal.current && idx < lockedIndices;
                        
                        return (
                             <span 
                                key={idx} 
                                className={`${style.char} ${isLocked ? style.revealed : style.hidden}`}
                            >
                                {charToDisplay}
                            </span>
                        );
                    })}
                </span>
            );

            globalIndex += word.length;

            if (wIdx < words.length - 1) {
                 globalIndex += 1; // Skip space
                 return (
                     <React.Fragment key={`f-${wIdx}`}>
                         {wordElement}
                         <span className={style.space}> </span>
                     </React.Fragment>
                 );
            }
            return wordElement;
        });
    };

    return (
        <span className={style.scrambleText}>
            {renderWords()}
        </span>
    );
};

const PodiumPlace = ({ winner, place, reveal, onComplete }) => {
    const heightMap = { 1: 200, 2: 140, 3: 90 };
    const [showBar, setShowBar] = useState(false);

    useEffect(() => {
        if (reveal) {
            setTimeout(() => setShowBar(true), 100);
        }
    }, [reveal]);

    return (
        <div className={`${style.podiumPlace} ${style[`place${place}`]}`}>
            <div className={style.nameContainer}>
                {reveal && (
                    <>
                        <div className={style.name}>
                            <SlotMachineText 
                                text={winner.name} 
                                isRevealed={true} 
                                delay={500}
                                onComplete={onComplete}
                            />
                        </div>
                        <div className={style.points}>{winner.points} pts</div>
                    </>
                )}
            </div>
            <div 
                className={style.bar} 
                style={{ height: showBar ? `${heightMap[place]}px` : '0px' }}
            >
                {place}
            </div>
        </div>
    );
};

const RaffleCard = ({ winner, delay }) => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setVisible(true);
        }, delay);
        return () => clearTimeout(timeout);
    }, [delay]);

    return (
        <div className={`${style.raffleCard} ${visible ? style.visible : ''}`}>
            <div className={style.name}>
                {visible && (
                    <SlotMachineText 
                        text={winner.name} 
                        isRevealed={true} 
                        delay={0}
                    />
                )}
            </div>
            <div className={style.label}>Raffle Winner</div>
        </div>
    );
};

const RafflePage = () => {
    const functions = getFunctions();
    const [leaderboardData, setLeaderboardData] = useState(null);
    const [raffleCount, setRaffleCount] = useState(5);
    const [isRunning, setIsRunning] = useState(false);
    
    // Game State
    const [top3, setTop3] = useState([]);
    const [raffleWinners, setRaffleWinners] = useState([]);
    const [revealStage, setRevealStage] = useState(0); // 0: Idle, 1: 3rd, 2: 2nd, 3: 1st, 4: Raffle
    const raffleSectionRef = useRef(null);

    const fetchLeaderboard = useCallback(async () => {
        let result;
        try {
            const func = httpsCallable(functions, "getLeaderboard");
            result = await func({ limit: 100000 });
        } catch(e) {
            console.error(e);
            return;
        }
        if(!result.data || !result.data.leaderboard) {
            console.log("Failed to fetch leaderboard");
            return;
        }
        return result.data.leaderboard;
    }, [functions]);

    useEffect(() => {
        const loadData = async () => {
            const data = await fetchLeaderboard();
            if(data) {
                setLeaderboardData(data); // already sorted on backend
            }
        };
        loadData();
    }, [fetchLeaderboard]);

    const runRaffle = () => {
        if (!leaderboardData || leaderboardData.length === 0) return;
        setIsRunning(true);
        setRevealStage(0);

        // Logic
        const sorted = [...leaderboardData].sort((a, b) => b.points - a.points);
        const top3Winners = sorted.slice(0, 3);
        
        const top3Ids = new Set(top3Winners.map(w => w.uid));
        
        // Candidates for raffle (excluding top 3)
        // Bucket sort strategy: each user has a range of points
        let candidates = sorted.filter(user => !top3Ids.has(user.uid) && user.points > 0);

        const selected = [];
        
        for (let i = 0; i < raffleCount && candidates.length > 0; i++) {
            const totalPoints = candidates.reduce((sum, c) => sum + c.points, 0);
            if (totalPoints === 0) break;

            const target = Math.random() * totalPoints;
            let runningSum = 0;
            
            for (let j = 0; j < candidates.length; j++) {
                runningSum += candidates[j].points;
                if (runningSum >= target) {
                    const winner = candidates[j];
                    selected.push(winner);
                    // Remove winner to ensure unique winners per raffle
                    candidates.splice(j, 1);
                    break;
                }
            }
        }

        setTop3(top3Winners);
        setRaffleWinners(selected);
        
        // Start Sequence
        startSequence();
    };

    const startSequence = () => {
        // Timeline
        // 0s: Start
        // 1s: Reveal 3rd
        // 4s: Reveal 2nd
        // 7s: Reveal 1st
        // 10s: Confetti & Raffle
        
        setTimeout(() => setRevealStage(1), 500); // 3rd
        setTimeout(() => setRevealStage(2), 3500); // 2nd
        setTimeout(() => setRevealStage(3), 6500); // 1st
        setTimeout(() => {
            fireConfetti();
            setRevealStage(4); // Raffle List
            setTimeout(() => {
                raffleSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 500);
        }, 10000);
    };

    const fireConfetti = () => {
        const duration = 3000;
        const animationEnd = Date.now() + duration;
        const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

        const randomInRange = (min, max) => Math.random() * (max - min) + min;

        const interval = setInterval(function() {
            const timeLeft = animationEnd - Date.now();

            if (timeLeft <= 0) {
                return clearInterval(interval);
            }

            const particleCount = 50 * (timeLeft / duration);
            confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
            confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
        }, 250);
    };

    if(leaderboardData === null) {
        return <div className={style.rafflePage}>
            <h2>Loading...</h2>
        </div>;
    }

    return (
        <div className={style.rafflePage}>
            <h1>Raffle & Reveal</h1>
            
            {!isRunning && (
                <div className={style.controls}>
                    <label>Display top 3, along with </label>
                    <Input 
                        type="number" 
                        value={raffleCount} 
                        onChange={(e) => setRaffleCount(parseInt(e.target.value) || 0)} 
                    />
                    <label>raffle winners</label>
                    <Button text="Run raffle" onClick={runRaffle} secondary={isRunning} />
                </div>
            )}

            <div className={style.podiumSection}>
                {isRunning && (
                    <div className={style.podiumContainer}>
                        {/* 2nd Place */}
                        {top3[1] && <PodiumPlace winner={top3[1]} place={2} reveal={revealStage >= 2} />}
                        
                        {/* 1st Place */}
                        {top3[0] && <PodiumPlace winner={top3[0]} place={1} reveal={revealStage >= 3} />}
                        
                        {/* 3rd Place */}
                        {top3[2] && <PodiumPlace winner={top3[2]} place={3} reveal={revealStage >= 1} />}
                    </div>
                )}
            </div>

            {revealStage >= 4 && (
                <div className={style.raffleSection} ref={raffleSectionRef}>
                    <h2>And {raffleWinners.length} Lucky Winners!</h2>
                    <div className={style.raffleGrid}>
                        {raffleWinners.map((winner, idx) => (
                            <RaffleCard 
                                key={winner.uid} 
                                winner={winner} 
                                delay={idx * 3000} // 3 seconds per winner for drama
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RafflePage;
